import {
  BdcBranch,
  BdcBranchUpsertRequest,
  BdcDc,
  BdcDcUpdateRequest,
  BdcSku,
  BdcSkuUpdateRequest,
} from "../../../types/bdc.type";

/** Select value for "no branch" on a DC row — sent as `company_id: null`. */
export const UNBOUND = "";

/**
 * The PATCH /bdc/dcs body for what changed on one DC row; empty when nothing did. `arCode` (the
 * accounting customer code of the ใบวางบิล) is left out when not given; blank clears it.
 */
export const dcUpdateBody = (dc: BdcDc, name: string, companyId: string, arCode?: string): BdcDcUpdateRequest => {
  const body: BdcDcUpdateRequest = {};
  const trimmed = name.trim();
  if (trimmed !== "" && trimmed !== dc.dc_name) body.dc_name = trimmed;
  if (companyId !== (dc.company_id ?? UNBOUND)) body.company_id = companyId === UNBOUND ? null : companyId;
  if (arCode !== undefined) {
    const code = arCode.trim().toUpperCase() || null;
    if (code !== (dc.ar_customer_code ?? null)) body.ar_customer_code = code;
  }
  return body;
};

/** bdc_dc.ar_customer_code varchar(20) — letters and digits (`1012C00001`). */
export const checkArCustomerCode = (text: string): string | null => {
  const code = text.trim();
  if (!code) return null;
  if (code.length > 20) return "ไม่เกิน 20 ตัวอักษร";
  if (!/^[A-Za-z0-9-]+$/.test(code)) return "ใช้ได้เฉพาะตัวอักษรอังกฤษและตัวเลข";
  return null;
};

/** The three ใบวางบิล / CN defaults of a BDC branch, as typed. */
export interface BranchDefaultsInput {
  cn_prefix: string;
  invoice_book_code: string;
  billing_label: string;
}

export const branchDefaultsInput = (branch: BdcBranch): BranchDefaultsInput => ({
  cn_prefix: branch.cn_prefix ?? "",
  invoice_book_code: branch.invoice_book_code ?? "",
  billing_label: branch.billing_label ?? "",
});

/** Why each typed default is refused (spec 1–2: CN prefix `^C[0-9]$`, book ≤ 10, label ≤ 20). */
export const branchDefaultsErrors = (input: BranchDefaultsInput): Partial<Record<keyof BranchDefaultsInput, string>> => {
  const errors: Partial<Record<keyof BranchDefaultsInput, string>> = {};
  const prefix = input.cn_prefix.trim().toUpperCase();
  if (prefix && !/^C[0-9]$/.test(prefix)) errors.cn_prefix = "C ตามด้วยตัวเลข 1 หลัก (เช่น C2)";
  const book = input.invoice_book_code.trim().toUpperCase();
  if (book && !/^[A-Z0-9]{1,10}$/.test(book)) errors.invoice_book_code = "ตัวอักษรอังกฤษ / ตัวเลข ไม่เกิน 10";
  const label = input.billing_label.trim();
  if (label.length > 20) errors.billing_label = "ไม่เกิน 20 ตัวอักษร";
  return errors;
};

/**
 * The PUT /bdc/branches body: the branch's test mode as it is plus the defaults that changed
 * (blank = null clears one). Null when nothing changed.
 */
export const branchDefaultsBody = (branch: BdcBranch, input: BranchDefaultsInput): BdcBranchUpsertRequest | null => {
  const next = {
    cn_prefix: input.cn_prefix.trim().toUpperCase() || null,
    invoice_book_code: input.invoice_book_code.trim().toUpperCase() || null,
    billing_label: input.billing_label.trim() || null,
  };
  const body: BdcBranchUpsertRequest = { is_test_mode: branch.is_test_mode };
  let changed = false;
  (Object.keys(next) as (keyof typeof next)[]).forEach((key) => {
    if (next[key] !== (branch[key] ?? null)) {
      body[key] = next[key];
      changed = true;
    }
  });
  return changed ? body : null;
};

/**
 * The PATCH /bdc/skus body for what changed on one SKU row; empty when nothing did. `perCase`
 * is null while the typed value is invalid; the GB link travels as a product code.
 */
export const skuUpdateBody = (
  sku: BdcSku,
  perCase: number | null,
  productCode: string | null,
): BdcSkuUpdateRequest => {
  const body: BdcSkuUpdateRequest = {};
  if (perCase !== null && perCase !== sku.pcs_per_case) body.pcs_per_case = perCase;
  if (productCode !== (sku.product_code ?? null)) body.product_code = productCode;
  return body;
};

/** The new pieces per tray when it changed and is valid (`perTray` null while invalid), else null. */
export const skuTrayUpdate = (sku: BdcSku, perTray: number | null): number | null =>
  perTray !== null && sku.pcs_per_tray != null && perTray !== sku.pcs_per_tray ? perTray : null;
